import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddTenantReportTemplate.css";
import { API_BASE } from "../../../ApiUrl";
import BrandingTabs from "../TenantBranding/BrandingTabs";

const API_URL = `${API_BASE}/tenant-report-templates/`;
const TENANT_RECORDS_API_URL = `${API_BASE}/tenant-records/`;

const emptyTemplate = {
  id: null,
  template_name: "",
  version: "",
  file: null,
  file_name: "",
  existing_file_url: ""
};

const AddTenantReportTemplate = () => {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState([{ ...emptyTemplate }]);
  const [tenantId, setTenantId] = useState(null);
  const [userId, setUserId] = useState(null);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  // ---- Get tenant + user ids ----
  useEffect(() => {
    const tenantUser = getTenantUser();
    if (tenantUser?.tenant_id) setTenantId(Number(tenantUser.tenant_id));
    if (tenantUser?.user_id) setUserId(Number(tenantUser.user_id));
  }, []);

  // ---- Fetch templates ----
  useEffect(() => {
    if (tenantId) fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        TENANT_RECORDS_API_URL,
        {
          tenant_id: tenantId,
          tables: ["tenant_report_templates"]
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const result = response.data;

      let rows = [];
      if (result?.data?.tenant_report_templates) {
        rows = result.data.tenant_report_templates;
      } else if (result?.tenant_report_templates) {
        rows = result.tenant_report_templates;
      } else if (Array.isArray(result?.data)) {
        rows = result.data;
      } else if (Array.isArray(result)) {
        rows = result;
      }

      setTemplates(rows);
    } catch (error) {
      console.error("Error fetching report templates:", error);
      Swal.fire({
        icon: "error",
        title: "Unable to load templates",
        text:
          error.response?.data?.detail ||
          "Something went wrong while loading templates."
      });
    } finally {
      setLoading(false);
    }
  };

  // ---- Form handlers ----
  const handleChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev.map((tpl, i) => (i === index ? { ...tpl, [name]: value } : tpl))
    );
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) =>
      prev.map((tpl, i) =>
        i === index
          ? {
              ...tpl,
              file,
              file_name: file ? file.name : tpl.file_name
            }
          : tpl
      )
    );
  };

  const handleAdd = () => {
    setFormData([{ ...emptyTemplate }]);
    setIsEditing(false);
    setEditingId(null);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setFormData((prev) => [...prev, { ...emptyTemplate }]);
  };

  const handleRemoveForm = (index) => {
    setFormData((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEdit = (tpl) => {
    setFormData([
      {
        id: tpl.id,
        template_name: tpl.template_name || "",
        version: tpl.version || "",
        file: null,
        file_name: tpl.file ? String(tpl.file).split("/").pop() : "",
        existing_file_url: tpl.file || ""
      }
    ]);
    setEditingId(tpl.id);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (tpl) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete Template?",
      text: `Are you sure you want to delete "${
        tpl.template_name || "this template"
      }"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}${tpl.id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Template deleted successfully.",
        timer: 1500,
        showConfirmButton: false
      });

      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text:
          error.response?.data?.detail ||
          "Unable to delete the template."
      });
    }
  };

  // ---- Validation ----
  const validateRow = (tpl, index) => {
    const label = `Template ${index + 1}`;

    const isEmptyRow =
      !tpl.template_name.trim() &&
      !tpl.version.trim() &&
      !tpl.file &&
      !tpl.file_name &&
      !tpl.id;
    if (isEmptyRow) return true;

    if (!tpl.template_name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Template Name for ${label}.`
      });
      return false;
    }

    if (tpl.template_name.trim().length > 255) {
      Swal.fire({
        icon: "warning",
        title: "Template Name Too Long",
        text: `Template Name for ${label} must be 255 characters or fewer.`
      });
      return false;
    }

    if (!tpl.version.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Version for ${label}.`
      });
      return false;
    }

    if (tpl.version.trim().length > 50) {
      Swal.fire({
        icon: "warning",
        title: "Version Too Long",
        text: `Version for ${label} must be 50 characters or fewer.`
      });
      return false;
    }

    if (!isEditing && !tpl.file) {
      Swal.fire({
        icon: "warning",
        title: "File Required",
        text: `Please upload a file for ${label}.`
      });
      return false;
    }

    if (tpl.file && tpl.file.size > 10 * 1024 * 1024) {
      Swal.fire({
        icon: "warning",
        title: "File Too Large",
        text: `File for ${label} must be 10 MB or smaller.`
      });
      return false;
    }

    return true;
  };

  const buildFormData = (tpl) => {
    const fd = new FormData();
    fd.append("template_name", tpl.template_name.trim());
    fd.append("version", tpl.version.trim());
    fd.append("tenant", tenantId);
    if (userId) fd.append("user", userId);

    if (tpl.file) {
      fd.append("file", tpl.file);
    }

    return fd;
  };

  const extractError = (error) => {
    if (error.response?.data) {
      const d = error.response.data;
      if (typeof d === "string") return d;
      if (d.detail) return d.detail;
      if (d.message) return d.message;
      if (d.errors) {
        return Object.entries(d.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${Array.isArray(errors) ? errors.join(", ") : errors}`
          )
          .join(" | ");
      }
      return JSON.stringify(d);
    }
    return error.message || "Unknown error";
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tenantId) {
      Swal.fire({
        icon: "error",
        title: "Tenant ID missing",
        text: "Unable to identify the current tenant."
      });
      return;
    }

    for (let i = 0; i < formData.length; i++) {
      if (!validateRow(formData[i], i)) return;
    }

    const activeRows = formData.filter(
      (tpl) =>
        tpl.template_name.trim() ||
        tpl.version.trim() ||
        tpl.file ||
        tpl.id
    );

    const MULTIPART_CONFIG = {
      headers: { "Content-Type": "multipart/form-data" }
    };

    try {
      setLoading(true);

      if (isEditing) {
        const fd = buildFormData(formData[0]);
        await axios.put(`${API_URL}${editingId}/`, fd, MULTIPART_CONFIG);

        await Swal.fire({
          icon: "success",
          title: "Updated Successfully",
          timer: 1500,
          showConfirmButton: false
        });
      } else {
        for (const tpl of activeRows) {
          const fd = buildFormData(tpl);
          await axios.post(API_URL, fd, MULTIPART_CONFIG);
        }

        await Swal.fire({
          icon: "success",
          title: "Created Successfully",
          text: `${activeRows.length} template${
            activeRows.length > 1 ? "s" : ""
          } created successfully.`,
          timer: 1500,
          showConfirmButton: false
        });
      }

      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      setFormData([{ ...emptyTemplate }]);

      await fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);

      Swal.fire({
        icon: "error",
        title: isEditing ? "Update failed" : "Creation failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData([{ ...emptyTemplate }]);
  };

  // ---- Lookup helpers ----
  const getFileName = (tpl) => {
    if (!tpl.file) return "-";
    return String(tpl.file).split("/").pop();
  };

  const handleViewDocument = (tpl) => {
    if (!tpl.file) {
      Swal.fire({
        icon: "info",
        title: "No file",
        text: "No file is attached to this template."
      });
      return;
    }

    const url = tpl.file.startsWith("http")
      ? tpl.file
      : `${API_BASE.replace(/\/api\/?$/, "")}${tpl.file}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-report-template-page">
          <BrandingTabs />

          {!showForm ? (
            <>
              {/* ==================================================
                  LIST HEADER
              ================================================== */}

              <div className="tenant-report-template-page-header">
                <div>
                  <h2>Report Templates</h2>
                </div>

                <button
                  type="button"
                  className="tenant-report-template-add-top-btn"
                  onClick={handleAdd}
                >
                  + Add Template
                </button>
              </div>

              {/* ==================================================
                  TABLE
              ================================================== */}

              <div className="tenant-report-template-list-card">
                {loading ? (
                  <div className="tenant-report-template-loading">
                    Loading templates...
                  </div>
                ) : templates.length === 0 ? (
                  <div className="tenant-report-template-empty">
                    <div className="tenant-report-template-empty-icon">+</div>
                    <h3>No Templates Found</h3>
                    <p>
                      There are no report templates available for the current
                      tenant.
                    </p>
                    <button
                      type="button"
                      className="tenant-report-template-empty-add-btn"
                      onClick={handleAdd}
                    >
                      + Add Template
                    </button>
                  </div>
                ) : (
                  <div className="tenant-report-template-table-wrapper">
                    <table className="tenant-report-template-table">
                      <thead>
                        <tr>
                          <th>Template Name</th>
                          <th>Version</th>
                          <th>File</th>
                          <th>Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {templates.map((tpl) => (
                          <tr key={tpl.id}>
                            <td>
                              <strong>{tpl.template_name || "-"}</strong>
                            </td>

                            <td>
                              <span className="tenant-report-template-version-pill">
                                {tpl.version || "-"}
                              </span>
                            </td>

                            <td>
                              {tpl.file ? (
                                <button
                                  type="button"
                                  className="tenant-report-template-view-btn"
                                  onClick={() => handleViewDocument(tpl)}
                                >
                                  View
                                </button>
                              ) : (
                                <span className="tenant-report-template-muted">
                                  -
                                </span>
                              )}
                            </td>

                            <td>
                              <div className="tenant-report-template-actions">
                                <button
                                  type="button"
                                  className="tenant-report-template-edit-btn"
                                  onClick={() => handleEdit(tpl)}
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="tenant-report-template-delete-btn"
                                  onClick={() => handleDelete(tpl)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* ==================================================
                  ADD / EDIT HEADER
              ================================================== */}

              <div className="tenant-report-template-page-header">
                <div>
                  <h2>
                    {isEditing ? "Edit Template" : "Create Templates"}
                  </h2>
                </div>

                <button
                  type="button"
                  className="tenant-report-template-back-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {/* ==================================================
                  FORM
              ================================================== */}

              <form onSubmit={handleSubmit}>
                {formData.map((tpl, index) => (
                  <div
                    className="tenant-report-template-card"
                    key={index}
                    style={{ marginBottom: "20px" }}
                  >
                    <div className="tenant-report-template-form-body">
                      <div className="tenant-report-template-record-header">
                        <h4>Template {index + 1}</h4>

                        {!isEditing && formData.length > 1 && (
                          <button
                            type="button"
                            className="tenant-report-template-remove-btn"
                            onClick={() => handleRemoveForm(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="tenant-report-template-form-grid">
                        <div className="tenant-report-template-form-group">
                          <label>
                            Template Name <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="template_name"
                            value={tpl.template_name}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. Site Inspection Report"
                            maxLength={255}
                          />
                        </div>

                        <div className="tenant-report-template-form-group">
                          <label>
                            Version <span>*</span>
                          </label>
                          <input
                            type="text"
                            name="version"
                            value={tpl.version}
                            onChange={(e) => handleChange(index, e)}
                            placeholder="e.g. v1.0"
                            maxLength={50}
                          />
                        </div>

                        <div className="tenant-report-template-form-group tenant-report-template-form-group--full">
                          <label>
                            File {!isEditing && <span>*</span>}
                          </label>

                          <div className="tenant-report-template-file-row">
                            <input
                              id={`tenant-report-template-file-${index}`}
                              type="file"
                              onChange={(e) => handleFileChange(index, e)}
                            />

                            {tpl.file_name && (
                              <span className="tenant-report-template-file-name">
                                {tpl.file_name}
                              </span>
                            )}
                          </div>

                          <p className="tenant-report-template-hint">
                            {isEditing
                              ? "Leave empty to keep the existing file."
                              : "Upload the report template file (PDF, DOCX, etc.)."}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditing && (
                  <button
                    type="button"
                    className="tenant-report-template-add-btn"
                    onClick={handleAddAnother}
                    style={{ marginBottom: "20px" }}
                  >
                    <span>+</span>
                    Add Another Template
                  </button>
                )}

                <div className="tenant-report-template-form-actions">
                  <button
                    type="button"
                    className="tenant-report-template-cancel-btn"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="tenant-report-template-save-btn"
                    disabled={loading}
                  >
                    {loading
                      ? "Saving..."
                      : isEditing
                      ? "Update Template"
                      : "Save Templates"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTenantReportTemplate;